import { incidentTripSchema } from './trip.js';
import { z } from 'zod';
import { paginationQuerySchema, paginated } from './common.js';
export const incidentStatusSchema = z.enum([
    'OPEN',
    'ACKNOWLEDGED',
    'IN_PROGRESS',
    'RESOLVED',
    'CLOSED',
]);
/**
 * The risk levels, in ASCENDING order of severity.
 *
 * Exported as a list because the ORDER is a fact callers need and a Zod enum
 * does not expose usefully — "is this below the category's floor" is a
 * comparison, not a membership test. Every screen that hand-wrote
 * `['LOW','MEDIUM','HIGH','CRITICAL']` was writing a second copy of this, and
 * one of them will eventually be reordered on its own.
 */
export const RISK_LEVELS = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
export const riskLevelSchema = z.enum(RISK_LEVELS);
/** Sortable columns for the incident list. */
export const incidentSortSchema = z.enum([
    'lastEventAt',
    'openedAt',
    'currentRiskScore',
    'status',
    'occurrenceCount',
]);
/**
 * Which kinds of incident a list means.
 *
 * ⚠️ Three values rather than a boolean, because "rule-derived only" and
 * "anomaly-derived only" and "both" are three different questions and a flag
 * can express two. `ANOMALY` exists so somebody can read the detector's output
 * as a queue without the rest of the fleet's incidents in the way — which is
 * the thing that was unreachable on any screen until this filter existed.
 */
export const INCIDENT_PROVENANCE_FILTERS = ['RULE', 'ANOMALY', 'ALL'];
export const incidentProvenanceFilterSchema = z.enum(INCIDENT_PROVENANCE_FILTERS);
/** What each choice means, so a screen never shows a reader a bare enum. */
export const INCIDENT_PROVENANCE_LABELS = {
    RULE: 'Found by rules',
    ANOMALY: 'Found by anomaly detection',
    ALL: 'Both',
};
export const incidentListQuerySchema = paginationQuerySchema
    .extend({
    sort: incidentSortSchema.default('lastEventAt'),
    status: incidentStatusSchema.optional(),
    riskLevel: riskLevelSchema.optional(),
    incidentType: z.string().optional(),
    vehicleId: z.string().uuid().optional(),
    driverId: z.string().uuid().optional(),
    assignedUserId: z.string().uuid().optional(),
    dateFrom: z.coerce.date().optional(),
    dateTo: z.coerce.date().optional(),
    search: z.string().trim().min(1).optional(),
    /**
     * How the incident was FOUND — the opt-in design D2 asks for.
     *
     * ⚠️ **The default is `RULE`, and that is the whole point of having a
     * default at all.** Incident counts appear on the dashboard, in the fleet
     * summary, in scheduled reports, in copilot answers and in the consolidated
     * organisation view. If baseline-derived incidents silently joined them,
     * every one of those figures would change meaning on the day anomaly
     * detection shipped and nobody would have decided that. An absent parameter
     * therefore means exactly what it meant before this filter existed — so
     * every saved link, every integrator's call and every stored view is
     * unaffected.
     *
     * ⚠️ **`ALL` is a deliberate choice a reader makes, never a convenience
     * default.** It is the only value that changes what an existing figure
     * means, so it has to be typed by somebody.
     */
    foundBy: incidentProvenanceFilterSchema.default('RULE'),
})
    .strict();
const assignedOperatorSchema = z
    .object({ id: z.string().uuid(), displayName: z.string(), email: z.string() })
    .nullable();
const vehicleRefSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    registration: z.string(),
});
const recommendationSummarySchema = z
    .object({
    id: z.string().uuid(),
    actionCategory: z.string(),
    recommendedAction: z.string(),
    status: z.string(),
    approvalRequired: z.boolean(),
    explanationSource: z.string(),
})
    .nullable();
export const incidentListItemSchema = z.object({
    id: z.string().uuid(),
    incidentType: z.string(),
    title: z.string(),
    summary: z.string().nullable(),
    status: incidentStatusSchema,
    riskLevel: riskLevelSchema,
    currentRiskScore: z.number().int(),
    occurrenceCount: z.number().int(),
    vehicle: vehicleRefSchema,
    assignedOperator: assignedOperatorSchema,
    latestRecommendation: recommendationSummarySchema,
    openedAt: z.string(),
    lastEventAt: z.string(),
    updatedAt: z.string(),
});
export const incidentListResponseSchema = paginated(incidentListItemSchema);
// ── detail ────────────────────────────────────────────────────────────────
const timelineEntrySchema = z.object({
    id: z.string().uuid(),
    at: z.string(),
    kind: z.string(),
    actorId: z.string().uuid().nullable(),
    detail: z.record(z.unknown()),
});
const contributingEventSchema = z.object({
    id: z.string().uuid(),
    /**
     * The raw payload this event was derived from, so the trail runs backwards.
     *
     * An incident's evidence is a CANONICAL event — mapped, normalised and given
     * OIP's vocabulary. For an audit that is one step removed from the artifact:
     * the question is what the provider actually sent, and only this id reaches
     * it. Not nullable — every canonical event is created with a
     * `rawProviderEventId` and the column is required.
     */
    rawProviderEventId: z.string().uuid(),
    eventType: z.string(),
    severity: z.string(),
    occurredAt: z.string(),
    measurements: z.record(z.unknown()),
    /** Circumstances the rule read — ignition, motion, the thresholds that applied. */
    attributes: z.record(z.unknown()),
    /** Where it happened, when the provider supplied it. Null is common and normal. */
    location: z.record(z.unknown()).nullable(),
    /**
     * What the CORRELATION rule concluded from this event.
     *
     * Distinct from `riskAssessments[].factors`, which is the POLICY's scoring
     * breakdown — a different calculation answering a different question. Confusing
     * the two is easy and was the reason the fuel classification appeared to be on
     * screen when it was not.
     *
     * NULL for links created before migration 0021, and NOT backfillable: these
     * were previously written only to an audit payload. A reader must treat
     * absence as "not captured", never as "no finding".
     */
    correlationFactors: z.record(z.unknown()).nullable(),
});
const riskAssessmentSchema = z.object({
    id: z.string().uuid(),
    riskLevel: riskLevelSchema,
    score: z.number().int(),
    factors: z.record(z.unknown()),
    calculationVersion: z.string(),
    assessedAt: z.string(),
});
const policyEvaluationSchema = z.object({
    id: z.string().uuid(),
    policyId: z.string().uuid(),
    policyVersionId: z.string().uuid(),
    /**
     * The rule's name and version, so the card does not ask a reader to recognise
     * a UUID. Same argument as policy learning, where a table of ids, counts and
     * rates was replaced because **nobody can retune a UUID**.
     *
     * Not nullable, unlike the outcome→version join: `PolicyEvaluation` holds real
     * foreign keys to both, and a version an evaluation names is one the discard
     * predicate refuses to delete. The row cannot outlive its rule.
     */
    policyName: z.string(),
    policyVersionNumber: z.number().int(),
    matched: z.boolean(),
    details: z.record(z.unknown()),
    evaluatedAt: z.string(),
});
const llmExplanationSchema = z
    .object({
    source: z.string(),
    headline: z.string().nullable(),
    explanation: z.string().nullable(),
    operatorSummary: z.string().nullable(),
    confidenceWording: z.string().nullable(),
    llmEnhanced: z.boolean(),
    llmFallbackUsed: z.boolean(),
    promptVersion: z.string().nullable(),
})
    .nullable();
export const incidentDetailSchema = incidentListItemSchema.extend({
    /**
     * The AI wording, when somebody has asked for one. NULL is the common state.
     *
     * ⚠️ Beside `title` and `summary`, never instead of them. Those are what the correlation
     * rule computed and what every list, export and integrator reads; a screen that showed
     * only the AI version would be unreadable the moment the LLM is switched off (D0).
     */
    aiSummary: z.string().nullable().default(null),
    aiHeadline: z.string().nullable().default(null),
    aiCurrentState: z.string().nullable().default(null),
    aiWordedAt: z.string().nullable().default(null),
    /**
     * Whether THIS reader may ask for a rewording.
     *
     * ⚠️ From the SERVER, never a client-side role comparison. A platform administrator holds
     * no membership in a tenant they have entered, so `role === 'TENANT_ADMIN'` answers false
     * for them while the guard admits them — and the page would then hide a control the server
     * accepts. That blindness is recorded five times in this codebase.
     */
    maySummarise: z.boolean().default(false),
    /**
     * The journey this incident happened during, or WHY there is none (D8).
     *
     * A trip is not another event to correlate; it is a FRAME the incident hangs
     * on. Present on the detail rather than as a timeline entry deliberately: a
     * timeline entry would have to be WRITTEN when the incident was raised, so an
     * incident whose trip was derived a moment later would carry none forever —
     * and rewriting one afterwards is exactly what D11 forbids. Read at display
     * time, it is always current and never re-judges anything.
     */
    trip: incidentTripSchema,
    timeline: z.array(timelineEntrySchema),
    contributingEvents: z.array(contributingEventSchema),
    riskAssessments: z.array(riskAssessmentSchema),
    policyEvaluations: z.array(policyEvaluationSchema),
    recommendations: z.array(z.object({
        id: z.string().uuid(),
        actionCategory: z.string(),
        recommendedAction: z.string(),
        status: z.string(),
        riskLevel: riskLevelSchema,
        riskScore: z.number().int(),
        approvalRequired: z.boolean(),
        explanationSource: z.string(),
        explanation: z.string().nullable(),
        createdAt: z.string(),
    })),
    llmExplanation: llmExplanationSchema,
    auditSummary: z.array(z.object({
        id: z.string().uuid(),
        action: z.string(),
        actorId: z.string().uuid().nullable(),
        at: z.string(),
    })),
});
/**
 * The AI summary of an incident — asked for, never raised.
 *
 * ⚠️ **`SUMMARISED` and `UNCHANGED` are different answers and must stay so.** One was written
 * just now; the other was written earlier and is still current because nothing the model
 * reads has changed. A reader deciding whether to trust prose about a LIVE incident needs to
 * know which, and a caller cannot report what it cannot distinguish.
 *
 * ⚠️ `SKIPPED` carries the gate's own word — GLOBAL_DISABLED, BUDGET_EXHAUSTED and the rest.
 * Without it "your allowance is spent" and "still working" are the same empty state, which
 * is the whole reason the outcome is recorded at all.
 *
 * ⚠️ `WORKING` is not a failure. The work happens in the worker (the API may not import the
 * gateway), so a caller that waited its budget and saw nothing yet is told exactly that
 * rather than being shown an error for a job still running.
 */
export const incidentSummaryOutcomeSchema = z.enum([
    'SUMMARISED',
    'UNCHANGED',
    'SKIPPED',
    'WORKING',
]);
export const incidentSummaryResponseSchema = z.object({
    outcome: incidentSummaryOutcomeSchema,
    headline: z.string().nullable().default(null),
    summary: z.string().nullable().default(null),
    currentState: z.string().nullable().default(null),
    /** The gate's word when SKIPPED; null otherwise. */
    reason: z.string().nullable().default(null),
    wordedAt: z.string().nullable().default(null),
});
// ── lifecycle actions ───────────────────────────────────────────────────────
export const incidentAssignRequestSchema = z.object({
    assigneeUserId: z.string().uuid(),
    note: z.string().max(500).optional(),
});
export const incidentActionRequestSchema = z.object({
    note: z.string().max(500).optional(),
});
export const incidentActionResponseSchema = z.object({
    id: z.string().uuid(),
    status: incidentStatusSchema,
    version: z.number().int(),
    updatedAt: z.string(),
});
//# sourceMappingURL=incident.js.map